import { CalendarComponent, ChangedEventArgs, NavigatedEventArgs } from '@syncfusion/ej2-react-calendars';
import React, { useState } from 'react';
import './EqxCalendar.css';

export interface EqxCalendarRange {
    start: Date;
    end: Date;
    isHalfDayStart?: boolean;
    isHalfDayEnd?: boolean;
    colour?: string;
}

export interface EqxCalendarProps {
    value?: Date;
    min?: Date;
    max?: Date;
    onChange?: (date: Date) => void;
    highlightRanges?: EqxCalendarRange[];
    disabledDates?: Date[];
    className?: string;
}

const EqxCalendar: React.FC<EqxCalendarProps> = ({
    value,
    min,
    max,
    onChange,
    highlightRanges,
    className,
}) => {
    // Track the current visible month
    const [viewDate, setViewDate] = useState<Date | undefined>(value);

    // Handler for date change
    const handleChange = (args: ChangedEventArgs) => {
        if (onChange && args.value instanceof Date) {
            onChange(args.value);
        }
        setViewDate(args.value instanceof Date ? args.value : undefined);
    };

    // Handler for navigation (month/year change)
    const handleNavigated = (args: NavigatedEventArgs) => {
        setViewDate(args.date);
    };

    // Handler to highlight date ranges
    const handleRenderDayCell = (args: any) => {
        if (highlightRanges && args.date && viewDate) {
            // Only highlight if in the current visible month
            if (args.date.getMonth() !== viewDate.getMonth() || args.date.getFullYear() !== viewDate.getFullYear()) {
                return;
            }
            highlightRanges.forEach(range => {
                const d = args.date;
                const colour = range.colour || '#ffe066';
                // Compare only date part
                const isInRange = d >= startOfDay(range.start) && d <= endOfDay(range.end);
                if (isInRange) {
                    args.element.classList.add('eqx-calendar-highlight');
                    args.element.style.setProperty('--eqx-calendar-highlight-bg', colour);
                    if (isSameDay(d, range.start)) {
                        args.element.classList.add('eqx-calendar-highlight-first');
                        if (range.isHalfDayStart) {
                            args.element.classList.add('eqx-calendar-highlight-halfday-first');
                        }
                    }
                    if (isSameDay(d, range.end)) {
                        args.element.classList.add('eqx-calendar-highlight-last');
                        if (range.isHalfDayEnd) {
                            args.element.classList.add('eqx-calendar-highlight-halfday-last');
                        }
                    }
                }
            });
        }
    };

    // Helper functions
    function isSameDay(a: Date, b: Date) {
        return a.getFullYear() === b.getFullYear() &&
            a.getMonth() === b.getMonth() &&
            a.getDate() === b.getDate();
    }
    function startOfDay(d: Date) {
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }
    function endOfDay(d: Date) {
        return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
    }

    return (
        <div className={className ? `eqx-calendar ${className}` : 'eqx-calendar'}>
            <CalendarComponent
                value={value}
                min={min}
                max={max}
                change={handleChange}
                navigated={handleNavigated}
                showTodayButton={true}
                cssClass="eqx-calendar-syncfusion"
                renderDayCell={handleRenderDayCell}
            />
        </div>
    );
};

export default EqxCalendar; 